import StringUtility from '@inter-ikea-kompis/utilities/lib/string/StringUtility';
import { LogoutItem } from 'sofa-reborn-react';

export class ApplicationUtils {
	/**
	 * Check if we are running in production environment.
	 *
	 * @returns True or false.
	 */
	public static isProduction(): boolean {
		return process.env.NODE_ENV === 'production';
	}

	public static googleAnalyticsExists(): boolean {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (!(window as any).ga) {
			return false;
		}

		return true;
	}

	public static cutLongText(textToCut: string, stripFromHere: number): string {
		return textToCut.length >= stripFromHere + 3
			? textToCut.substring(0, stripFromHere) + '...'
			: textToCut;
	}

	public static isIOS(): boolean {
		return (
			(/iPad|iPhone|iPod/.test(navigator.userAgent) && !self['MSStream']) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
		);
	}

	public static isAndroid(): boolean {
		return /android/i.test(navigator.userAgent);
	}

	public static isMobile(): boolean {
		return this.isIOS() || this.isAndroid();
	}

	public static getOSVersion(): { major: number; minor: number; patch: number } | null {
		if (this.isMobile()) {
			const ua = navigator.userAgent.toLowerCase();
			const regexp = this.isAndroid() ? /android\s([0-9\.]*)/i : /version\/([0-9\.]*)/i;
			const match = ua.match(regexp);
			return match && this.parseOsVersion(match[1]);
		}
		return null;
	}

	public static isOsVersion(osVersion: string): boolean {
		const version = this.getOSVersion();
		const minVersion = this.parseOsVersion(osVersion);
		if (version && minVersion) {
			return (
				version.major > minVersion.major ||
				(version.major === minVersion.major && version.minor > minVersion.minor) ||
				(version.major === minVersion.major &&
					version.minor === minVersion.minor &&
					version.patch >= minVersion.patch)
			);
		}
		return false;
	}

	public static isModelVersion(models: string[], notModels?: string[]): boolean {
		const platform = this.getPlatform();
		if (platform) {
			const ignore = notModels || [];
			return (
				!ignore.some((model) => platform.includes(model)) &&
				models.some((model) => platform.includes(model))
			);
		}
		return false;
	}

	public static createUrlParameters(data: LogoutItem): string {
		const snakeData = this.renameKeysToSnake(data);
		return Object.entries(snakeData)
			.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
			.join('&');
	}

	private static renameKeysToSnake(obj: LogoutItem): LogoutItem {
		const keyValues = Object.keys(obj).map((key) => {
			const newKey = StringUtility.camelToSnake(key);
			return { [newKey]: obj[key as keyof Record<string, unknown>] };
		});
		return Object.assign({}, ...keyValues);
	}

	private static parseOsVersion(
		osVersion: string
	): { major: number; minor: number; patch: number } | null {
		const version = { major: 0, minor: 0, patch: 0 };
		const versions = osVersion.split('.');
		if (versions) {
			version.major = parseInt(versions[0]) || 0;
			version.minor = parseInt(versions[1]) || 0;
			version.patch = parseInt(versions[2]) || 0;
			return version;
		}
		return null;
	}

	private static getPlatform(): string | null {
		if (/iPhone/.test(navigator.userAgent)) {
			const screen = window.screen;
			switch (`${screen.width}x${screen.height}:${window.devicePixelRatio}`) {
				case '390x844:3':
					return 'iPhone 12, iPhone 12 Pro, iPhone 13, iPhone 13 Pro';
				case '428x926:3':
					return 'iPhone 12 Pro Max, iPhone 13 Pro Max';
				case '414x896:3':
					return 'iPhone XS Max, iPhone 11 Pro Max';
				case '375x812:3':
					return 'iPhone X, iPhone Xs, iPhone 11 Pro, iPhone 12 mini, iPhone 13 mini';
				case '414x896:2':
					return 'iPhone XR, iPhone 11';
				case '414x736:3':
					return 'iPhone 8 Plus';
				case '375x667:2':
					return 'iPhone 8, iPhone SE';
				default:
					return 'unknown';
			}
		}

		return null;
	}
}
